import React, { useState, useEffect, useRef } from 'react';
import BBITable from '../../../helpers/bBITable/BBITable';
import classesCustom from '../../../helpers/search/CustomSearch.module.css';
import classesFinancialReport from './FinancialReport.module.css';
import FilterData from '../../../helpers/filter/FilterData';
import Get from '../../../api/internal/Get';
import Header from '../../../layouts/Header';
import View from '../../../helpers/slab/View';
import ViewDriver from '../../../features/views/users/driver/Driver';
import { CSVLink } from 'react-csv';
import { MdDownload } from 'react-icons/md';
import {
  GenerateQueryString,
  GetLastFullWeekMonday,
  ParseUrlForSearchParams,
} from '../../../helpers/search/HelperFunctions';
import Select from '../../../assets/essentials/Select';
import OptionList from '../../../helpers/options/OptionList';
import Input from '../../../assets/essentials/Input';
import Button from '../../../assets/essentials/Button';
import { useOutletContext } from 'react-router-dom';

function DriverFinancials({ toggleBoardRefresh }) {
  const [isLoading, setIsLoading] = useState<boolean>();
  const [driverFinancialsBoard, setDriverFinancialsBoard] = useState<
    Array<DriversFinancialsBoard>
  >([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const [branchOptions, setBranchOptions] = useState([]);
  const isFirstRender = useRef(true);
  const filterValue = useOutletContext();

  const emptySearchParams = {
    branchId: null,
    employeeId: null,
    dateMin: null, //must use name "dateMin" if we want to use defaultSearchDate
    dateMax: null,
  };
  const [driverFinancialSearchParams, setDriverFinancialSearchParams] =
    useState(emptySearchParams);

  useEffect(() => {
    setIsLoading(true);
    if (driverOptions.length === 0) {
      GetDriverOptions();
    }
    if (branchOptions.length === 0) {
      GetBranchList();
    }
    const { branchId, employeeId, dateMin, dateMax } = ParseUrlForSearchParams(
      emptySearchParams,
      GetLastFullWeekMonday(),
      isFirstRender.current,
    );

    setDriverFinancialSearchParams({
      branchId: branchId,
      employeeId: employeeId,
      dateMin: dateMin,
      dateMax: dateMax,
    });
    SearchDriverFinancialReport({
      branchId: branchId,
      employeeId: employeeId,
      dateMin: dateMin,
      dateMax: dateMax,
    });
    isFirstRender.current = false;
  }, [toggleBoardRefresh]);

  function SearchDriverFinancialReport(SearchParamsFromForm?: Object) {
    const searchParams = SearchParamsFromForm
      ? SearchParamsFromForm
      : driverFinancialSearchParams;
    const queryString = '?' + GenerateQueryString(searchParams);
    const newUrl =
      window.location.origin + window.location.pathname + queryString;
    window.history.replaceState(null, null, newUrl);
    Get(`/Accounting/SearchDriverFinancialReport${queryString}`).then(
      response => {
        if (response) {
          setDriverFinancialsBoard(response.data);
          setIsLoading(false);
        }
      },
    );
  }

  function GetDriverOptions() {
    Get(`/Employee/GetDriverOptions/`).then(response => {
      if (response) {
        setDriverOptions(response?.data);
      }
    });
  }
  function GetBranchList() {
    Get(`/Load/GetBranchList`).then(response => {
      if (response) {
        setBranchOptions(response.data);
      }
    });
  }

  function ClearSearchParams() {
    setDriverFinancialSearchParams(emptySearchParams);
  }

  function HandleFormSubmit(e) {
    e.preventDefault();
    setIsLoading(true);
    const formData = {
      branchId: e.target.branchId?.value,
      employeeId: e.target.employeeId?.value,
      dateMin: e.target.dateMin?.value,
      dateMax: e.target.dateMax?.value,
    };
    setDriverFinancialSearchParams(formData);
    SearchDriverFinancialReport(formData);
  }

  const formattedBoard = driverFinancialsBoard.map(driverFinancial => ({
    ...driverFinancial,
    revenue: driverFinancial.revenue.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),

    estFuel: driverFinancial.estFuel.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),

    lumper: driverFinancial.lumper.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),

    washOut: driverFinancial.washOut.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),

    payroll: driverFinancial.payroll.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),

    breakdown: driverFinancial.breakdown.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),

    payable: driverFinancial.payable.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    margin: driverFinancial.margin.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    }),
    marginPercentage: (driverFinancial.marginPercentage * 100).toFixed(1),
    miles: driverFinancial.miles.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 1,
    }),
  }));

  const columnConfig = [
    {
      key: '1',
      attribute: 'driverName',
      attributeprimaryhidden: 'employeeId',
      header: 'Driver',
      onClick: e =>
        View(
          <ViewDriver
            employeeId={e.target.dataset.attributeprimaryhidden}
            driverName={e.target.innerText}
          />,
        ),
      dataType: 'number',
    },
    {
      key: '2',
      attribute: 'branchName',
      header: 'Branch',
    },
    {
      key: '3',
      attribute: 'dispatchName',
      header: 'Current Dispatch',
    },
    {
      key: '4',
      attribute: 'payrollStatus',
      header: 'Payroll Status',
    },
    {
      key: '5',
      attribute: 'loadCount',
      header: 'Load Count',
      includeInTotal: true,
    },
    {
      key: '6',
      attribute: 'miles',
      header: 'Miles',
      dataType: 'formattedNumber',
      includeInTotal: true,
    },
    {
      key: '7',
      attribute: 'revenue',
      header: 'Revenue',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '8',
      attribute: 'lumper',
      header: 'Lumper',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '9',
      attribute: 'washOut',
      header: 'Wash Out',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '10',
      attribute: 'estFuel',
      header: 'Est. Fuel',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '11',
      attribute: 'payrollDeadheadQuantity',
      header: 'Paid DH Miles',
      includeInTotal: true,
    },
    {
      key: '12',
      attribute: 'payroll',
      header: 'Payroll',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '13',
      attribute: 'breakdown',
      header: 'Breakdown',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '14',
      attribute: 'payable',
      header: 'Payable',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '15',
      attribute: 'margin',
      header: 'Margin',
      dataType: 'currency',
      includeInTotal: true,
    },
    {
      key: '16',
      attribute: 'marginPercentage',
      header: 'Margin %',
      dataType: 'number',
      includeInTotal: true,
    },
  ];

  let tableData = FilterData(formattedBoard, filterValue);

  const SearchForm = () => {
    return (
      <div className={classesCustom.customSearch}>
        <form onSubmit={e => HandleFormSubmit(e)}>
          <label>
            Branch
            <Select
              defaultValue={driverFinancialSearchParams.branchId || ''}
              name="branchId"
            >
              <option value="">All Branches</option>
              <OptionList
                optionData={branchOptions}
                attributeID="branchId"
                attributeName="branchName"
              />
            </Select>
          </label>
          <label>
            Driver
            <Select
              defaultValue={driverFinancialSearchParams.employeeId || ''}
              name="employeeId"
            >
              <option value="">All Drivers</option>
              <OptionList
                optionData={driverOptions}
                attributeID="employeeId"
                attributeName="name"
              />
            </Select>
          </label>

          <label>
            Pickup Date Start
            <Input
              type="date"
              name="dateMin"
              defaultValue={driverFinancialSearchParams.dateMin}
            />
          </label>
          <label>
            Pickup Date End
            <Input
              type="date"
              name="dateMax"
              defaultValue={driverFinancialSearchParams.dateMax}
            />
          </label>

          <div className={classesCustom.submitHolder}>
            <Button type="submit" variant="good">
              Search
            </Button>

            <Button
              onClick={() => ClearSearchParams()}
              type="button"
              variant="bad"
            >
              Clear
            </Button>
          </div>
        </form>
      </div>
    );
  };

  const Summary = () => {
    const loadCount =
      tableData?.reduce((sum, row) => sum + row.loadCount, 0) ?? 0;
    const totalRevenue =
      tableData?.reduce(
        (sum, row) => sum + Number(row.revenue.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalEstFuel =
      tableData?.reduce(
        (sum, row) => sum + Number(row.estFuel.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalPayroll =
      tableData?.reduce(
        (sum, row) => sum + Number(row.payroll.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalPayable =
      tableData?.reduce(
        (sum, row) => sum + Number(row.payable.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const totalMargin =
      tableData?.reduce(
        (sum, row) => sum + Number(row.margin.replace(/[^\d.-]/g, '')),
        0,
      ) ?? 0;
    const marginPercentage =
      totalRevenue === 0 ? 0 : ((totalMargin / totalRevenue) * 100).toFixed(2);

    return (
      <div className={classesFinancialReport.summaryRow}>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Load Count:
          </span>
          {loadCount}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Revenue:
          </span>
          {totalRevenue.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Est. Fuel:
          </span>
          {totalEstFuel.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Payroll:
          </span>
          {totalPayroll.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Payable:
          </span>
          {totalPayable.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>
            Total Margin:
          </span>
          {totalMargin.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
          })}
        </div>
        <div>
          <span className={classesFinancialReport.summaryLabel}>Margin:</span>{' '}
          {marginPercentage}%
        </div>
      </div>
    );
  };

  const csvFileName =
    new Date().toISOString().split('T')[0].replace(/[-]/g, '') +
    '_Driver Financial Report.csv';

  return (
    <>
      <Header>
        <button>
          <CSVLink
            data={tableData}
            headers={[
              { label: 'Driver', key: 'driverName' },
              { label: 'Branch', key: 'branchName' },
              { label: 'Current Dispatch', key: 'dispatchName' },
              { label: 'Payroll Status', key: 'payrollStatus' },
              { label: 'Load Count', key: 'loadCount' },
              { label: 'Miles', key: 'miles' },
              { label: 'Revenue', key: 'revenue' },
              { label: 'Lumper', key: 'lumper' },
              { label: 'Wash Out', key: 'washOut' },
              { label: 'Est. Fuel', key: 'estFuel' },
              { label: 'Paid DH Miles', key: 'payrollDeadheadQuantity' },
              { label: 'Payroll', key: 'payroll' },
              { label: 'Breakdown', key: 'breakdown' },
              { label: 'Payable', key: 'payable' },
              { label: 'Margin', key: 'margin' },
              { label: 'Margin Percentage', key: 'marginPercentage' },
            ]}
            filename={csvFileName}
            className={classesFinancialReport.downloadButton}
          >
            <MdDownload />
            <span>Download</span>
          </CSVLink>
        </button>
      </Header>
      <SearchForm />
      <Summary />
      {isLoading ? (
        <h3>Generating Report...</h3>
      ) : (
        <BBITable data={tableData} columns={columnConfig} />
      )}
    </>
  );
}

export default DriverFinancials;
